import type {
  CodexCapacitySeries,
  PublicStatusBucketRecord,
  PublicStatusState,
  TenantCodexCapacityResponse,
} from '@nadoutong/contracts';

/**
 * Backend default tiers: normal below 15, elevated below 30, high from 30.
 * Ops can retune the server-side cuts; these stay the tenant display cuts.
 */
export const CAPACITY_ELEVATED = 15;
export const CAPACITY_HIGH = 30;

const STATE_RANK: Record<PublicStatusState, number> = {
  healthy: 0,
  busy: 1,
  unavailable: 2,
};

/**
 * Map an at-capacity ratio onto the availability vocabulary.
 * `null` (under-sampled) is not a tier and must not read as 0 / healthy.
 */
export function capacityTier(ratio: number | null): PublicStatusState | null {
  if (ratio === null) return null;
  if (ratio < CAPACITY_ELEVATED) return 'healthy';
  if (ratio < CAPACITY_HIGH) return 'busy';
  return 'unavailable';
}

/**
 * Worst of the two states. `healthy` < `busy` < `unavailable`.
 * A null capacity tier leaves availability untouched.
 */
export function worseState(
  availability: PublicStatusState,
  capacity: PublicStatusState | null,
): PublicStatusState {
  if (capacity === null) return availability;
  return STATE_RANK[capacity] > STATE_RANK[availability] ? capacity : availability;
}

/**
 * For each 30-minute availability bucket, the max non-null 15-minute
 * ratio whose `t_start` falls in `[bucket_start, bucket_end)`.
 * Matched by timestamp, not by index. All-null (or no overlap) is null.
 */
export function foldCapacityToBuckets(
  series: CodexCapacitySeries,
  history: readonly PublicStatusBucketRecord[],
): (number | null)[] {
  return history.map((bucket) => {
    const start = Date.parse(bucket.bucket_start);
    const end = Date.parse(bucket.bucket_end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    let max: number | null = null;
    for (const point of series.buckets) {
      const t = Date.parse(point.t_start);
      if (!Number.isFinite(t) || t < start || t >= end) continue;
      if (point.ratio === null) continue;
      if (max === null || point.ratio > max) max = point.ratio;
    }
    return max;
  });
}

/** `by_model[].sku` is the same string as a status row's `sku_name`. */
export function capacityBySku(
  response: TenantCodexCapacityResponse,
): Map<string, CodexCapacitySeries> {
  const bySku = new Map<string, CodexCapacitySeries>();
  for (const model of response.by_model) {
    bySku.set(model.sku, model);
  }
  return bySku;
}

function seriesHasSample(series: CodexCapacitySeries): boolean {
  if (series.ratio_24h !== null) return true;
  for (const bucket of series.buckets) {
    if (bucket.ratio !== null) return true;
  }
  return false;
}

/**
 * True when any at-capacity ratio in the payload is non-null.
 * An all-null response is under-sampled and must not surface capacity UI.
 */
export function hasCapacitySample(response: TenantCodexCapacityResponse): boolean {
  if (seriesHasSample(response.overall)) return true;
  for (const model of response.by_model) {
    if (seriesHasSample(model)) return true;
  }
  return false;
}
